import { useCallback } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import AriesIpcRenderer from '@renderer/ipc/AriesIpc.renderer'
import { isMac } from '@shared/utils/platform'
import type { PermissionsState } from '@shared/types/AudioCapturePermissions'
import { useMirroredPermission } from './use-mirrored-permission'

const checkMicrophonePerm = (): Promise<boolean> => AriesIpcRenderer.checkMicrophonePerm()
const selectMicrophoneAccess = (permissions: PermissionsState): boolean | undefined => permissions.microphoneAccess

/**
 * Tracks the macOS microphone permission via the main-owned permission mirror.
 *
 *  - `request()` triggers the native prompt when undetermined; once denied the
 *    native side deep-links to System Settings (macOS won't re-prompt).
 */
export function useMicrophonePermission(enabled = true) {
  const { status, check, isLoaded } = useMirroredPermission<boolean>({
    select: selectMicrophoneAccess,
    checkPerm: checkMicrophonePerm,
    fallback: false,
    enabled,
  })

  const request = useCallback(async () => {
    if (!isMac || !enabled) {
      return
    }
    try {
      const outcome = await AriesIpcRenderer.requestMicrophonePerm()
      if (outcome === 'error') {
        Sentry.captureMessage('Microphone permission request failed')
      }
      await check()
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [check, enabled])

  return {
    isAuthorized: status === true,
    /** Applicable only on macOS (Windows grants implicitly). */
    isApplicable: isMac,
    isLoaded,
    check,
    request,
  }
}
